/**
 * Site Settings V2 — "Sharing" section (staff-only): third-party
 * customer access to this site, sharing the same `<ThirdPartiesTab>`
 * the EditSiteDialog Sharing tab uses.
 *
 * Contains:
 * - AccessSection (default export)
 */

import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import StatusChip from '@sophocles/components/StatusChip';
import { SettingsSection } from '@sophocles/views/settings/components';
import ThirdPartiesTab from '@sophocles/views/sites/components/StructureCard/ThirdPartiesTab';

// Access section.  Mounts the same `<ThirdPartiesTab>` the
// EditSiteDialog Sharing tab uses so the V2 surface and the dialog
// stay in lock-step (external + internal share lists, add row,
// vendor cap, etc.).
//
// Passing `onChange` to ThirdPartiesTab folds pending share
// additions / removals into the Formik values under the synthetic
// `__pending_shares` key (deep-equal-tracked so the Save bar
// activates whenever a row is added or marked for unshare).  The
// V2 save flow strips that key out of the settings PATCH and
// dispatches `shareVirtualAccess` / `unshareVirtualAccess` after
// the consolidated endpoint succeeds — same split the dialog uses
// (the consolidated endpoint has no writable scalars on
// `third_party` post-migration; it's a presence-only marker for
// admin visibility).
//
// `siteGroup` is the plain `{id, description}` pair the parent view
// resolves via useSiteGroup — ThirdPartiesTab's site-id path uses it
// to label the destination and exclude the parent group from the
// internal-share picker while its own site-snapshot fetch is still
// in flight.
export default function AccessSection({ customerId, siteId, siteGroup, resetPendingVersion = 0 }) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const handleChange = React.useCallback(
    changes => {
      setFieldValue(
        '__pending_shares',
        {
          newlyShared: changes?.newlyShared ?? [],
          newlyUnshared: changes?.newlyUnshared ?? [],
        },
        false,
      );
    },
    [setFieldValue],
  );
  return (
    <SettingsSection
      id="settings-third-party"
      icon={<VerifiedUserIcon />}
      title={t('siteSettings.v2.access', 'Sharing')}
      description={t('siteSettings.v2.accessDescription', 'Allow other customers to view this site.')}
      chip={<StatusChip color="info" label={t('siteSettings.staffOnly', 'Staff Only')} />}
    >
      <ThirdPartiesTab
        customerId={customerId}
        siteId={siteId}
        siteGroup={siteGroup}
        onChange={handleChange}
        resetPendingVersion={resetPendingVersion}
      />
    </SettingsSection>
  );
}
